// src/components/Layout.tsx - Updated to handle Microsoft authentication
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileButton from '../profileButton/ProfileButton';
import axios from 'axios';
import { refreshToken } from '../../hooks/useAuth';
import { getStorage } from '../../hooks/useAuth';
import { API_CONFIG } from '../../apis/api-config';
import './header.css';
import { useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';

interface LayoutProps {
    children: React.ReactNode;
}

interface Organization {
    id: number;
    name: string;
    lastVisited: string;
    role: number;
}

interface UserData {
    name: string;
    activeOrganization: {
        id: number;
        name: string;
    };
    usersOrganizations: Organization[];
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const { currentUser } = useSelector((state: RootState) => state.currentUser);
    const navigate = useNavigate();
    const [user, setUser] = useState<UserData | null>(null);
    const [organization, setOrganization] = useState<string | undefined>('');
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);

    // Get authentication details
    const token = getStorage().getItem('access_token');
    const environment = getStorage().getItem('environment');
    const storedUser = getStorage().getItem('user');

    const handleLogoClick = () => {
        navigate('/home');
    };

    // Setup axios defaults
    useEffect(() => {
        if (token) {
            axios.defaults.headers.common.Accept = 'application/json';
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
    }, [token]);

    const handleOrganizationChange = async (selectedOrg: Organization) => {
        try {
            // POST to switch organization - only send the ID
            await axios.post(
                `${API_CONFIG.BASE_URL}/user/current/organization`,
                selectedOrg.id,
                {
                    timeout: API_CONFIG.TIMEOUT,
                    headers: { 'Content-Type': 'application/json' }
                }
            );

            // Update local state after successful API call
            setOrganization(selectedOrg.name);
            setIsDropdownOpen(false);
            
            // Optional: Refresh user data to get updated assets/info
            await fetchUser();
            navigate('/home')
            window.location.reload();
            console.log('Organization switched successfully!');

        } catch (error) {
            console.error('Error switching organization:', error);
            alert('Failed to switch organization. Please try again.');
        }
    };

    const fetchUser = async () => {
        console.log('Fetching user data####',  currentUser);
        setOrganization(currentUser?.activeOrganization?.name);
        setOrganizations(currentUser?.usersOrganizations || []);
        setAuthError(null);
    };

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            if (!target.closest('.organization-dropdown')) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        fetchUser();
    }, [currentUser]);

    // Show error state if authentication fails
    if (authError) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column',
                fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>
                <div style={{ 
                    color: 'red', 
                    fontSize: '18px', 
                    marginBottom: '10px' 
                }}>
                    {authError}
                </div>
                <button 
                    onClick={() => navigate('/welcome')}
                    style={{
                        backgroundColor: '#007cba',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Back to Login
                </button>
            </div>
        );
    }

    return (
        <div>
            <header className="global-header">
                <div className="header-content">
                    <div className="logo-section">
                        <button
                            onClick={handleLogoClick}
                            className="logo-button"
                        >
                            <img
                                src="/img/EM_symbol_White.png"
                                alt="EM Logo"
                                className="logo-image"
                            />
                        </button>
                    </div>
                    
                    <div className="organization-dropdown">
                        <button
                            onClick={toggleDropdown}
                            className="organization-button"
                        >
                            <span className="organization-name">{organization || 'Loading...'}</span>
                            <svg
                                className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="currentColor"
                            >
                                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" />
                            </svg>
                        </button>

                        {isDropdownOpen && (
                            <div className="dropdown-menu">
                                {organizations.map((org) => (
                                    <button
                                        key={org.id}
                                        onClick={() => handleOrganizationChange(org)}
                                        className={`dropdown-item ${org.name === organization ? 'active' : ''}`}
                                    >
                                        {org.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    <div className="user-stuff">
                        <ProfileButton />
                    </div>
                </div>
            </header>

            <main className="main-content">
                <div className="page-container">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default Layout;